import { useCallback, useEffect, useId, useRef, type ReactNode } from "react";
import { Button } from "./Button";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute("hidden") && el.getAttribute("aria-hidden") !== "true",
  );
}

export interface DialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children?: ReactNode;
  /** Closing while an action is in flight would leave the user unsure whether it ran. */
  dismissible?: boolean;
}

/**
 * Modal dialog: focus moves in on open, is trapped while open, and returns to
 * the element that opened it on close. Esc and the backdrop close it unless the
 * dialog is non-dismissible (an action is in flight).
 */
export function Dialog({
  open,
  title,
  description,
  onClose,
  footer,
  children,
  dismissible = true,
}: DialogProps) {
  const panel = useRef<HTMLDivElement | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descId = useId();

  const close = useCallback(() => {
    if (dismissible) onClose();
  }, [dismissible, onClose]);

  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Focus the panel itself rather than its first control, so assistive
    // technology announces the title and description before any action.
    panel.current?.focus();
    return () => {
      opener.current?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const node = panel.current;
      if (!node) return;
      const items = focusable(node);
      if (items.length === 0) {
        event.preventDefault();
        node.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === node)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, close]);

  if (!open) return null;

  return (
    // The backdrop is a pointer affordance only. Keyboard users close the
    // dialog with Escape or the close button, so it needs no role of its own.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions
    <div className="ods-dialog__backdrop" onMouseDown={close} data-testid="dialog-backdrop">
      {/* The panel stops the backdrop's pointer handler; it is not itself interactive. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="ods-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        ref={panel}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="ods-dialog__header">
          <h2 className="ods-dialog__title" id={titleId}>
            {title}
          </h2>
          <button
            type="button"
            className="ods-dialog__close"
            aria-label="Close dialog"
            onClick={close}
            disabled={!dismissible}
          >
            ×
          </button>
        </div>
        {description ? (
          <div className="ods-dialog__description" id={descId}>
            {description}
          </div>
        ) : null}
        <div className="ods-dialog__body">{children}</div>
        {footer ? <div className="ods-dialog__footer">{footer}</div> : null}
      </div>
    </div>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  busyLabel?: string;
  disabled?: boolean;
  error?: ReactNode;
  children?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Dialog plus the confirm/cancel footer every governed action shares. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  busyLabel,
  disabled = false,
  error,
  children,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      title={title}
      description={description}
      onClose={onCancel}
      dismissible={!busy}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            onClick={onConfirm}
            disabled={busy || disabled}
          >
            {busy ? (busyLabel ?? "Working…") : confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {error ? (
        <div className="ods-dialog__error" role="alert">
          {error}
        </div>
      ) : null}
    </Dialog>
  );
}
